/**
 * Static fallback dataset for the cases-on-map widget.
 *
 * Used in two situations:
 *   1. As a deliberate "offline" mode — the widget can be told to skip the
 *      API and render this data immediately (handy for the standalone
 *      landing page and for local dev without a backend).
 *   2. As a last-ditch fallback when the API throws. The widget logs a
 *      console warning and silently swaps in this dataset so the page never
 *      goes blank.
 *
 * The shape mirrors `MapDataset` from `./api.ts` so callers can treat both
 * sources identically. Keep this file pure — no React, no DOM, no fetches —
 * so it's trivially testable and tree-shakable.
 */
import type { MapCasePoint, MapDataset } from "./api";
import berlinPointsJson from "./data/berlin-points.json";

/**
 * Bundled Berlin dataset, loaded from `data/berlin-points.json` at build
 * time. Edit the JSON file to add / replace points — keep this file pure
 * TypeScript so the validation below stays readable.
 *
 * The JSON is validated once at module load so a malformed entry fails
 * loudly (clear console error + thrown exception) rather than silently
 * rendering a broken map.
 */
function validatePoints(raw: unknown): MapCasePoint[] {
  if (!Array.isArray(raw)) {
    throw new Error(
      "[conny] berlin-points.json must be an array of MapCasePoint",
    );
  }
  return raw.map((p, i) => {
    const e = p as Record<string, unknown>;
    const num = (k: string) => {
      const v = e[k];
      if (typeof v !== "number" || !Number.isFinite(v)) {
        throw new Error(
          `[conny] berlin-points.json[${i}].${k} must be a finite number`,
        );
      }
      return v;
    };
    const str = (k: string) => {
      const v = e[k];
      if (typeof v !== "string") {
        throw new Error(
          `[conny] berlin-points.json[${i}].${k} must be a string`,
        );
      }
      return v;
    };
    const years = e.years;
    if (!Array.isArray(years) || years.some((y) => typeof y !== "number")) {
      throw new Error(
        `[conny] berlin-points.json[${i}].years must be an array of numbers`,
      );
    }
    const landlords = e.landlords;
    if (
      landlords == null ||
      typeof landlords !== "object" ||
      Array.isArray(landlords)
    ) {
      throw new Error(
        `[conny] berlin-points.json[${i}].landlords must be an object`,
      );
    }
    return {
      street: str("street"),
      house_number: str("house_number"),
      zip_code: str("zip_code"),
      lat: num("lat"),
      lng: num("lng"),
      total: num("total"),
      cases: num("cases"),
      cws: num("cws"),
      closed: num("closed"),
      open: num("open"),
      landlords: landlords as Record<string, number>,
      years: years as number[],
    };
  });
}

export const STATIC_BERLIN_POINTS: MapCasePoint[] =
  validatePoints(berlinPointsJson);

/** Bounding boxes + default zoom centres for known cities. */
export const CITY_CENTERS: Record<
  string,
  {
    center: [number, number];
    zoom: number;
    bbox: [number, number, number, number];
  }
> = {
  berlin: {
    center: [52.52, 13.405],
    zoom: 11,
    // [west, south, east, north]
    bbox: [13.0883, 52.3382, 13.7611, 52.6755],
  },
};

export function staticDatasetFor(city: string): MapDataset {
  const normalized = (city || "berlin").toLowerCase();
  const meta = CITY_CENTERS[normalized] ?? CITY_CENTERS.berlin;
  // We currently only ship a Berlin seed dataset. For any other city we
  // still surface those points (rather than rendering an empty map) so the
  // landing page never looks broken when the API is unreachable. Once the
  // backend endpoint ships, this fallback should rarely be hit anyway.
  const points = STATIC_BERLIN_POINTS;
  return {
    city: normalized,
    points,
    bbox: meta
      ? {
          west: meta.bbox[0],
          south: meta.bbox[1],
          east: meta.bbox[2],
          north: meta.bbox[3],
        }
      : null,
  };
}

/** Filter a dataset to points within `radiusKm` of (lat, lng). */
export function filterByRadius(
  dataset: MapDataset,
  centerLat: number,
  centerLng: number,
  radiusKm: number,
): MapDataset {
  if (!Number.isFinite(radiusKm) || radiusKm <= 0) return dataset;
  const points = dataset.points.filter(
    (p) => haversineKm(centerLat, centerLng, p.lat, p.lng) <= radiusKm,
  );
  return { ...dataset, points };
}

/** Filter a dataset to points whose ZIP code starts with the given prefix. */
export function filterByZip(dataset: MapDataset, zip: string): MapDataset {
  const z = String(zip || "").trim();
  if (!z) return dataset;
  const points = dataset.points.filter((p) => p.zip_code === z);
  return { ...dataset, points };
}

/**
 * Status filters selectable via the widget's `data-filter` attribute.
 * "won-with-savings" keeps only buildings with at least one closed case
 * that recorded savings (`cws > 0`) — the wins-showcase view. Replaces the
 * landing page's window.fetch shim, and unlike that shim it also applies
 * to the bundled fallback dataset.
 */
export type MapStatusFilter = "won-with-savings";

export function filterByStatus(
  dataset: MapDataset,
  filter: MapStatusFilter | undefined,
): MapDataset {
  if (filter !== "won-with-savings") return dataset;
  const points = dataset.points.filter((p) => p.cws > 0);
  return { ...dataset, points };
}

/** Great-circle distance between two WGS84 points, in kilometres. */
export function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
